import { getAnalytics } from './analytics';
import {
  AnalyticsEventProperties,
  AnalyticsTarget,
  UnionOfEventsWithProperties,
} from './types';
import { useCallback } from 'react';

const isBrowser = globalThis.document !== undefined;

export const getCommonProperties = () => {
  if (!isBrowser) return {};
  return {
    initial_referrer: document.referrer,
  };
};

const getTrackingProperties = (additionalProps = {}) => ({
  ...getCommonProperties(),
  ...additionalProps,
});

export const useAnalytics = <TEvents extends string, TEventsWithProps>(
  additionalProperties?: Record<string, unknown>,
) => {
  // const analyticsContext = await getAnalytics();

  // const { track, identify, page } = analyticsContext;

  const trackPageView = useCallback(async () => {
    const { page } = await getAnalytics();
    // Properties go in the 3rd slot, where the wrapper merges super properties
    page(undefined, undefined, getTrackingProperties(additionalProperties));
  }, [additionalProperties]);

  const identifyUser = async (
    userId: string,
    userTraits?: Record<string, unknown>,
  ) => {
    const { identify } = await getAnalytics();

    identify(userId, {
      ...getTrackingProperties(additionalProperties),
      ...userTraits,
    });
  };
  // Type for the overloaded trackEvent function
  type TrackEvent = {
    <T extends AnalyticsTarget<TEvents, TEventsWithProps>>(
      event: UnionOfEventsWithProperties<T['EventsWithProps']>,
      properties: UnionOfEventsWithProperties<T['EventsWithProps']>,
    ): void;
    <
      T extends AnalyticsTarget<TEvents, TEventsWithProps>,
      EventName extends Exclude<
        T['Events'],
        keyof UnionOfEventsWithProperties<T['EventsWithProps']>
      >,
    >(
      event: EventName,
      properties?: AnalyticsEventProperties<
        TEvents,
        TEventsWithProps,
        T,
        EventName
      >,
    ): void;
  };

  const trackEvent = useCallback(
    async <
      T extends AnalyticsTarget<TEvents, TEventsWithProps>,
      EventName extends T['Events'],
    >(
      event: EventName,
      properties: AnalyticsEventProperties<
        TEvents,
        TEventsWithProps,
        T,
        EventName
      > = {} as AnalyticsEventProperties<
        TEvents,
        TEventsWithProps,
        T,
        EventName
      >,
    ) => {
      const { track } = await getAnalytics();
      const props =
        properties && typeof properties === 'object' ? properties : {};

      track(event, {
        ...getTrackingProperties(additionalProperties),
        ...props,
      });
    },
    [additionalProperties],
  ) as TrackEvent;

  return {
    trackEvent,
    trackPageView,
    identifyUser,
  };
};
